import { DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TypographyComponent } from 'components';
import { MentionCardComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/mention-cards-section/mention-card.component';
import { FindExportView } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-find-mentions.model';
import { SocialListeningMention } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import {
	FindMentionsService,
	mapFindMention
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/find-mentions.service';
import { findFilterChips } from './find-filter-labels';

/** Gotenberg gives up on its own; the page still says it is done before that. */
const EXPORT_TIMEOUT_MS = 15000;
const IMAGE_WAIT_MS = 4000;

declare global {
	interface Window {
		findMentionsExportReady?: boolean;
	}
}

/**
 * The headless page Gotenberg prints for Find mentions' Download PDF: the
 * question, the query it was read as, the filters and window, and the selected
 * mention cards. Opened outside the app shell with an export token as its only
 * credential, like the AI Summary export page.
 */
@Component({
	standalone: true,
	selector: 'app-find-mentions-export',
	imports: [NgIf, NgFor, DatePipe, TypographyComponent, MentionCardComponent],
	template: `
		<div class="export-page" *ngIf="view && !errorText">
			<div class="export-head">
				<typography variant="p/sm_medium" class="muted">
					Social Listening · Find mentions
				</typography>
				<typography variant="h/2" [ariaLevel]="1">
					{{ view.question }}
				</typography>
				<div class="facts">
					<typography *ngIf="view.query_text" variant="p/xs_regular">
						Query: {{ view.query_text }}
					</typography>
					<typography *ngIf="filtersText" variant="p/xs_regular">
						Filters: {{ filtersText }}
					</typography>
					<typography variant="p/xs_regular">
						Last {{ view.window_days }} days · exported
						{{ view.exported_at | date: 'd MMM y, HH:mm' }} ·
						{{ countText }}
					</typography>
				</div>
			</div>

			<app-mention-card
				*ngFor="let mention of mentions; trackBy: trackById"
				[authorName]="mention.authorName"
				[authorUsername]="mention.authorUsername"
				[authorImage]="mention.authorImage"
				[authorVerified]="mention.authorVerified"
				[authorUrl]="mention.authorUrl"
				[authorPlatform]="mention.authorPlatform"
				[authorPlatformId]="mention.authorPlatformId"
				[postUrl]="mention.postUrl"
				[postCaption]="mention.postCaption"
				[matchedKeywords]="mention.matchedKeywords"
				[title]="mention.title"
				[domainVisits]="mention.domainVisits"
				[postImage]="mention.postImage"
				[postLikes]="mention.postLikes"
				[postComments]="mention.postComments"
				[postViews]="mention.postViews"
				[postEngagementRate]="mention.postEngagementRate"
				[postEstimatedReach]="mention.postEstimatedReach"
				[postDate]="mention.postDate"
				[sentiment]="mention.sentiment"
				[origin]="mention.origin"
				[parentUrl]="mention.parentUrl"
				[showCardActions]="false"
				[showCardMenu]="false"
				[fixedHeight]="false"
				[fullText]="true"
			></app-mention-card>
		</div>

		<div class="export-error" *ngIf="errorText">
			<typography variant="p/base_medium">{{ errorText }}</typography>
		</div>
	`,
	styles: [
		`
			@page {
				margin: 2rem;
			}

			:host {
				display: block;
				// CONTRACT: the PDF renderer prints A4 at 96dpi, 793px wide, minus the 2rem @page margins on each side.
				width: 729px;
				background: #fff;

				// TRAP: down to the page end, like the AI Summary export — the app body behind it is grey and prints.
				.export-page {
					display: flex;
					flex-direction: column;
					row-gap: 1rem;
					min-height: 100vh;
					background: #fff;

					.export-head {
						display: flex;
						flex-direction: column;
						row-gap: 0.375rem;
						padding-bottom: 1rem;
						border-bottom: 1px solid #ebebf0;

						.muted {
							color: #7a7a7a;
						}

						.facts {
							display: flex;
							flex-direction: column;
							row-gap: 0.125rem;

							typography {
								color: #4b5563;
							}
						}
					}

					app-mention-card {
						break-inside: avoid;
					}
				}

				.export-error {
					padding: 2rem;
				}
			}
		`
	]
})
export class FindMentionsExportComponent implements OnInit, OnDestroy {
	view: FindExportView | null = null;
	mentions: SocialListeningMention[] = [];
	filtersText = '';
	countText = '';
	errorText = '';

	private unsubscriber = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private findMentionsService: FindMentionsService,
		private zone: NgZone
	) {}

	ngOnInit(): void {
		// CONTRACT: the backend's Gotenberg call waits for this flag (find/pdf_export.py).
		window.findMentionsExportReady = false;
		setTimeout(() => this.markReady(), EXPORT_TIMEOUT_MS);

		const searchId = this.route.snapshot.paramMap.get('searchId') ?? '';
		const exportToken =
			this.route.snapshot.queryParamMap.get('exportToken') ?? '';
		if (!searchId || !exportToken) {
			this.fail('Missing export parameters.');
			return;
		}

		this.findMentionsService
			.getExportView(searchId, exportToken)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: view => {
					this.view = view;
					this.mentions = view.mentions.map(mention =>
						mapFindMention(mention)
					);
					this.filtersText = findFilterChips(view.filters)
						.map(chip => chip.label)
						.join(', ');
					const count = this.mentions.length;
					this.countText = `${count} ${
						count === 1 ? 'mention' : 'mentions'
					}`;
					// ORDER: images are in the DOM only after this render pass.
					this.zone.runOutsideAngular(() =>
						setTimeout(() => this.waitForImages(), 0)
					);
				},
				error: () => this.fail('Failed to load the export.')
			});
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	trackById(_index: number, mention: SocialListeningMention): string {
		return mention.id;
	}

	private waitForImages(): void {
		const pending = Array.from(
			document.querySelectorAll<HTMLImageElement>('img')
		).filter(image => !image.complete);
		const loaded = Promise.all(
			pending.map(
				image =>
					new Promise<void>(resolve => {
						image.addEventListener('load', () => resolve(), {
							once: true
						});
						image.addEventListener('error', () => resolve(), {
							once: true
						});
					})
			)
		);
		const timeout = new Promise<void>(resolve =>
			setTimeout(resolve, IMAGE_WAIT_MS)
		);
		Promise.race([loaded, timeout]).then(() => this.markReady());
	}

	private fail(message: string): void {
		this.errorText = message;
		this.markReady();
	}

	private markReady(): void {
		window.findMentionsExportReady = true;
	}
}
